import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, unwrap } from '../../lib/api/client'
import {
  dismissSentMailApiV1SentMailSuggestionIdDismissPost,
  fileSentMailApiV1SentMailSuggestionIdFilePost,
  listCaseSentMailApiV1CasesCaseIdSentMailGet,
  releaseSentMailApiV1SentMailSuggestionIdReleasePost,
} from '../../lib/api/generated'

/** Mail a tech sent or received outside Desk, waiting on this case (#1024). */

export const sentMailKeys = {
  case: (caseId: string) => ['sent-mail', caseId] as const,
}

export const caseSentMailQuery = (caseId: string) =>
  queryOptions({
    queryKey: sentMailKeys.case(caseId),
    queryFn: () => unwrap(listCaseSentMailApiV1CasesCaseIdSentMailGet({ path: { case_id: caseId } })),
    staleTime: 15_000,
    retry: (count, error) => {
      if (error instanceof ApiError && (error.notBuilt || error.status === 404)) return false
      return count < 1
    },
  })

function refresh(queryClient: ReturnType<typeof useQueryClient>, caseId: string) {
  void queryClient.invalidateQueries({ queryKey: sentMailKeys.case(caseId) })
  void queryClient.invalidateQueries({ queryKey: ['case'] })
  void queryClient.invalidateQueries({ queryKey: ['notifications'] })
}

export function useFileSentMail(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (suggestionId: string) =>
      unwrap(
        fileSentMailApiV1SentMailSuggestionIdFilePost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human', case_id: caseId },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}

export function useDismissSentMail(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (suggestionId: string) =>
      unwrap(
        dismissSentMailApiV1SentMailSuggestionIdDismissPost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human' },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}

export function useReleaseSentMail(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (suggestionId: string) =>
      unwrap(
        releaseSentMailApiV1SentMailSuggestionIdReleasePost({
          path: { suggestion_id: suggestionId },
          body: { initiated_by: 'human' },
        }),
      ),
    onSuccess: () => refresh(queryClient, caseId),
  })
}
